import { Link } from "react-router-dom";
import { Building2, Shield, Zap, Scale, Wrench, TrendingUp, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteForm from "@/components/QuoteForm";
import { trackWhatsApp } from "@/components/layout/WhatsAppButton";

const OFFICE_IMG = "https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHBlb3BsZSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzczNDMyMzMxfDA&ixlib=rb-4.1.0&q=85";

const coverages = [
  { icon: Building2, title: "Patrimônio", desc: "Proteção contra incêndio, roubo, danos elétricos e mais para o imóvel e bens da empresa." },
  { icon: Scale, title: "Responsabilidade Civil", desc: "Cobertura para danos involuntários causados a terceiros durante a operação." },
  { icon: Wrench, title: "Equipamentos", desc: "Proteção para máquinas, equipamentos e tecnologia da sua empresa." },
  { icon: TrendingUp, title: "Lucros Cessantes", desc: "Garantia de receita em caso de interrupção das atividades por sinistro." },
  { icon: Zap, title: "Danos Elétricos", desc: "Cobertura para danos causados por oscilação de energia, raios e curto-circuito." },
  { icon: Shield, title: "Vendaval e Granizo", desc: "Proteção contra fenômenos naturais que podem danificar o imóvel." },
];

export default function SeguroEmpresarial() {
  return (
    <div data-testid="seguro-empresarial-page">
      {/* Hero */}
      <section className="relative pt-20">
        <div className="bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 mb-5">
                  <Building2 className="h-3.5 w-3.5 text-gold" />
                  <span className="text-xs text-white/80 font-medium">Seguro Empresarial</span>
                </div>
                <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-5">
                  Seguro Empresarial para <span className="text-gold">todo o Brasil</span>
                </h1>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
                  Proteja o patrimônio, equipamentos e operações da sua empresa com coberturas completas e custo-benefício incomparável.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/cotacao"><Button className="btn-gold h-11 px-6" data-testid="emp-cta-cotacao">Solicitar Cotação <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
                  <a href="https://wa.me/553125550207?text=Preciso%20de%20seguro%20empresarial" target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp('seguro_empresarial')}>
                    <Button className="btn-whatsapp h-11 px-6" data-testid="emp-cta-whatsapp">Falar com Especialista</Button>
                  </a>
                </div>
              </div>
              <div className="hidden lg:block">
                <img src={OFFICE_IMG} alt="Seguro Empresarial" className="rounded-2xl shadow-2xl w-full h-72 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverages */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Coberturas</p>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy">Proteção completa para sua empresa</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coverages.map((c, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-11 h-11 rounded-lg bg-brand/10 flex items-center justify-center mb-4">
                  <c.icon className="h-5 w-5 text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-navy mb-2">{c.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + Form */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm font-semibold text-gold uppercase tracking-wider mb-3">Para todos os portes</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy mb-6">Seguro para pequenas, médias e grandes empresas</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                Independente do porte da sua empresa, oferecemos soluções de seguro adequadas para cada necessidade e orçamento.
              </p>
              <ul className="space-y-3">
                {["Comércios e lojas", "Escritórios e consultórios", "Indústrias e fábricas", "Restaurantes e bares", "Prestadores de serviços", "Empresas de tecnologia"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-brand shrink-0" /> <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border p-6 md:p-8 shadow-sm">
              <h3 className="font-heading font-semibold text-navy text-xl mb-5">Cotação - Seguro Empresarial</h3>
              <QuoteForm preselect="Seguro Empresarial" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
